import { cloneElement, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

export function DropdownMenu({ trigger, children, align = 'end', label = 'Menú de acciones' }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const focusFirstItem = window.requestAnimationFrame(() => {
      menuRef.current?.querySelector('[role="menuitem"]:not(:disabled)')?.focus();
    });
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFirstItem);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const moveFocus = (event) => {
    const items = [...menuRef.current.querySelectorAll('[role="menuitem"]:not(:disabled)')];
    if (!items.length) return;
    const currentIndex = items.indexOf(document.activeElement);
    let nextIndex = currentIndex;

    if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % items.length;
    else if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + items.length) % items.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = items.length - 1;
    else if (event.key === 'Tab') {
      setOpen(false);
      return;
    } else return;

    event.preventDefault();
    items[nextIndex]?.focus();
  };

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div ref={rootRef} className="relative inline-flex">
      {cloneElement(trigger, {
        ref: triggerRef,
        'aria-expanded': open,
        'aria-haspopup': 'menu',
        onClick: (event) => {
          trigger.props.onClick?.(event);
          setOpen((current) => !current);
        },
        onKeyDown: (event) => {
          trigger.props.onKeyDown?.(event);
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
          }
        },
      })}
      {open ? (
        <div
          ref={menuRef}
          role="menu"
          aria-label={label}
          onKeyDown={moveFocus}
          className={clsx(
            'absolute top-[calc(100%+0.5rem)] z-40 min-w-56 overflow-hidden rounded-xl border border-[rgba(var(--border),0.72)] bg-[rgb(var(--panel))] p-1.5 text-[rgb(var(--fg))] shadow-[0_12px_30px_rgba(var(--shadow),0.16)]',
            align === 'start' ? 'left-0' : 'right-0',
          )}
        >
          {typeof children === 'function' ? children({ close }) : children}
        </div>
      ) : null}
    </div>
  );
}

export function DropdownMenuItem({
  children,
  icon: Icon,
  danger = false,
  disabled = false,
  onSelect,
  className,
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={() => onSelect?.()}
      className={clsx(
        'flex min-h-10 w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-45',
        danger
          ? 'text-red-700 hover:bg-red-500/10 dark:text-red-300 dark:hover:bg-red-400/10'
          : 'text-[rgb(var(--fg))] hover:bg-[rgba(var(--subpanel),0.88)]',
        className,
      )}
    >
      {Icon ? <Icon aria-hidden="true" className="h-4 w-4 shrink-0" /> : null}
      <span>{children}</span>
    </button>
  );
}

export function DropdownMenuSeparator() {
  return <div role="separator" className="my-1.5 h-px bg-[rgba(var(--border),0.58)]" />;
}

export default DropdownMenu;
